import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MailerService } from 'src/app/services/mailer.service';
import { CreateFacilityUserComponent } from '../create-facility-user/create-facility-user.component';
import { PatientOverviewData } from '../patient-overview/patient-overview.models';
import { SharingPatientId, SharingSetupComponent } from '../sharing-setup/sharing-setup.component';
import { FacilitatorInfo, FacilityInfo } from '../transfer-visualization/transfer-visualization.component';
import { OldPatientOverviewFacade } from './old-patient-overview.facade';
import { OldPatientOverviewData } from './old-patient-overview.models';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { FirebaseUtilitiesService } from 'src/app/services/firebase-utilities.service';

@Component({
    selector: 'app-old-patient-overview',
    templateUrl: './old-patient-overview.component.html',
    styleUrls: ['./old-patient-overview.component.scss'],
    providers: [OldPatientOverviewFacade],
    standalone: false
})
export class OldPatientOverviewComponent implements OnDestroy {
  @ViewChild('imageShareRight', { static: false }) imageShareRight!: ElementRef;
  @ViewChild('orthancFrame', { static: false }) orthancFrame?: ElementRef<HTMLIFrameElement>;
  @ViewChild(SharingSetupComponent, { static: false }) sharingSetupComponent?: SharingSetupComponent;
  @Input() orthancUrl: string = '';

  /** True when the studies page has loaded results spanning more than one unique patient. */
  studiesHaveMultiplePatients = false;
  /** Patient derived from the studies results when all results are for the same patient. */
  studiesFoundPatient: PatientOverviewData['patient'] | null = null;

  private backToPatientOverviewHandler?: (event: Event) => void;
  private lastBtpoEventTimestamp: number = 0;
  private readonly BTPO_DEBOUNCE_MS = 2000; // Prevent multiple calls within 2 seconds
  private isProcessingBtpo: boolean = false; // Flag to prevent concurrent processing

  constructor(
    public dialogRef: MatDialogRef<OldPatientOverviewComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: OldPatientOverviewData,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private facade: OldPatientOverviewFacade,
    private router: Router,
    private dialog: MatDialog,
    private mailer_$: MailerService,
    private localStorageService: LocalStorageService
  ) {
    this.facade.initialize(this.dialogRef, data);
    this.initializeFromUrl();
    this.setupExtensionEventListener();
  }

  /**
   * Setup event listener for Chrome extension messages
   * Listens for "Back to Patient Overview" button click from extension
   */
  private setupExtensionEventListener(): void {
    console.log('[Patient Overview] ===== Setting up extension event listener =====');
    console.log('[Patient Overview] Component initialized, setting up listeners...');

    // Store the handler so we can remove it later
    this.backToPatientOverviewHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventTimestamp = customEvent.detail?.timestamp || Date.now();

      // Prevent concurrent processing
      if (this.isProcessingBtpo) {
        console.log('[Patient Overview] ⚠ Ignoring event - already processing BtPO');
        return;
      }

      // Debounce: ignore if called within the debounce window
      const timeSinceLastCall = Date.now() - this.lastBtpoEventTimestamp;
      if (timeSinceLastCall < this.BTPO_DEBOUNCE_MS) {
        console.log(
          '[Patient Overview] ⚠ Ignoring duplicate event (debounced). Time since last call:',
          timeSinceLastCall,
          'ms'
        );
        return;
      }

      // Set flags to prevent duplicate processing
      this.isProcessingBtpo = true;
      this.lastBtpoEventTimestamp = Date.now();

      console.log('[Patient Overview] ===== Back to Patient Overview event received from extension =====');
      console.log('[Patient Overview] Event detail:', customEvent.detail);

      // Fire the handler
      this.onBackToPatientOverviewFromExtension();

      // Reset processing flag after a short delay to allow for async operations
      setTimeout(() => {
        this.isProcessingBtpo = false;
      }, 100);
    };

    // Listen only on window (events bubble, so this catches all dispatches)
    window.addEventListener('nuagedx-back-to-patient-overview', this.backToPatientOverviewHandler);

    console.log('[Patient Overview] ✓ Extension event listeners set up for "Back to Patient Overview"');
    console.log('[Patient Overview] Listening on window and document for event: nuagedx-back-to-patient-overview');

    // Expose a test function to window for debugging
    (window as any).testBackToPatientOverviewEvent = () => {
      console.log('[Patient Overview] TEST: Manually triggering Back to Patient Overview event');
      const testEvent = new CustomEvent('nuagedx-back-to-patient-overview', {
        detail: { source: 'test', timestamp: Date.now() }
      });
      window.dispatchEvent(testEvent);
      document.dispatchEvent(testEvent);
    };

    // Expose a verification function to check if listeners are set up
    (window as any).checkBackToPatientOverviewListeners = () => {
      console.log('[Patient Overview] ===== Checking event listeners =====');
      console.log('[Patient Overview] Handler function exists:', !!this.backToPatientOverviewHandler);
      console.log('[Patient Overview] Component instance:', this);
      console.log('[Patient Overview] Component is initialized:', !!this.vm);
      console.log('[Patient Overview] Current URL:', window.location.href);
      console.log('[Patient Overview] Document ready state:', document.readyState);

      // Try to manually check if listeners are attached (this is tricky in JavaScript)
      // We can't directly inspect event listeners, but we can verify the handler exists
      if (this.backToPatientOverviewHandler) {
        console.log('[Patient Overview] ✓ Handler function is defined');
        console.log('[Patient Overview] Handler type:', typeof this.backToPatientOverviewHandler);
      } else {
        console.error('[Patient Overview] ✗ Handler function is NOT defined!');
      }

      // Test if we can dispatch and receive an event
      console.log('[Patient Overview] Testing event dispatch...');
      const testEvent = new CustomEvent('nuagedx-back-to-patient-overview', {
        detail: { source: 'verification-test', timestamp: Date.now() }
      });
      console.log('[Patient Overview] Dispatching test event...');
      window.dispatchEvent(testEvent);
      document.dispatchEvent(testEvent);
      console.log('[Patient Overview] Test event dispatched. Check above for handler execution.');

      return {
        handlerExists: !!this.backToPatientOverviewHandler,
        componentInitialized: !!this.vm,
        url: window.location.href,
        readyState: document.readyState
      };
    };

    console.log('[Patient Overview] DEBUG: Test function available at window.testBackToPatientOverviewEvent()');
    console.log(
      '[Patient Overview] DEBUG: Verification function available at window.checkBackToPatientOverviewListeners()'
    );

    // If BtPO happened before this component mounted, consume the one-shot flag now.
    this.consumePendingBtpoFlag();
  }

  /**
   * Consume the BtPO one-shot flag that the extension stores in localStorage.
   * This makes the behavior robust to timing (event fired before component mounted).
   */
  private consumePendingBtpoFlag(): void {
    try {
      const raw = localStorage.getItem('nuagedx_btpo_pending');
      if (!raw) return;

      let payload: any = null;
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = { raw };
      }

      // Guard against stale triggers (e.g., from a previous session)
      const ts = typeof payload?.timestamp === 'number' ? payload.timestamp : 0;
      const ageMs = ts ? Date.now() - ts : Number.POSITIVE_INFINITY;

      // Only consume flags that are very recent (within 5 seconds) to avoid stale flags
      if (ageMs > 5 * 1000) {
        // Flag is too old, clear it and ignore
        localStorage.removeItem('nuagedx_btpo_pending');
        console.warn('[Patient Overview] BtPO flag found but too old; ignoring and clearing.', { payload, ageMs });
        return;
      }

      // Check if this flag was already consumed in this session
      const consumedKey = `nuagedx_btpo_consumed_${ts}`;
      const alreadyConsumed = sessionStorage.getItem(consumedKey);

      if (alreadyConsumed) {
        // Already consumed in this session, clear the flag and ignore
        localStorage.removeItem('nuagedx_btpo_pending');
        console.warn('[Patient Overview] BtPO flag already consumed in this session; ignoring.', { payload, ageMs });
        return;
      }

      // Clean up old sessionStorage entries (older than 1 hour)
      try {
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('nuagedx_btpo_consumed_')) {
            const timestamp = parseInt(key.replace('nuagedx_btpo_consumed_', ''));
            if (timestamp && timestamp < oneHourAgo) {
              sessionStorage.removeItem(key);
            }
          }
        }
      } catch (e) {
        // Ignore cleanup errors
      }

      // Mark as consumed in this session
      sessionStorage.setItem(consumedKey, 'true');

      // Clear the flag immediately to ensure one-shot behavior
      localStorage.removeItem('nuagedx_btpo_pending');

      console.log('[Patient Overview] ✓ Consumed pending BtPO flag from localStorage', { payload, ageMs });

      // Check if already processing to avoid duplicate calls
      if (!this.isProcessingBtpo) {
        this.isProcessingBtpo = true;
        this.lastBtpoEventTimestamp = Date.now();
        this.onBackToPatientOverviewFromExtension();
        setTimeout(() => {
          this.isProcessingBtpo = false;
        }, 100);
      }
    } catch (e) {
      console.warn('[Patient Overview] Could not consume BtPO flag from localStorage:', e);
      // Clear the flag on error to prevent it from sticking
      try {
        localStorage.removeItem('nuagedx_btpo_pending');
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Handle "Back to Patient Overview" event from Chrome extension
   * This method is called when the "Back to Patient Overview" button is clicked in the external app
   */
  onBackToPatientOverviewFromExtension(): void {
    console.log('[Patient Overview] ===== onBackToPatientOverviewFromExtension() called =====');
    console.log('[Patient Overview] Back to Patient Overview button was clicked in external app');

    // Get patient ID and facility ID from current URL or component data
    const currentUrl = this.router.url;
    let patientID: string | null = null;
    let facilityID: string | null = null;

    // Try to extract from current URL first
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      patientID = urlMatch[1];
    }

    const facilityMatch = currentUrl.match(/\/exchange\/([^\/]+)/);
    if (facilityMatch && facilityMatch[1]) {
      facilityID = facilityMatch[1];
    }

    // Fallback to component data if not in URL
    if (!patientID && this.vm.patient?.patientId) {
      patientID = this.vm.patient.patientId;
    }

    if (!facilityID && this.vm.selectedTargetFacility?.facility) {
      facilityID = this.vm.selectedTargetFacility.facility;
    }

    // If we still don't have both, try to get from saved state
    if (patientID && !facilityID) {
      const savedState = this.localStorageService.getPatientOverviewState(patientID);
      if (savedState?.selectedTargetFacility?.facility) {
        facilityID = savedState.selectedTargetFacility.facility;
      }
    }

    // Navigate to the URL with /send at the end
    if (patientID && facilityID) {
      const targetUrl = `/patient-overview-modal/${patientID}/exchange/${facilityID}/images/send`;
      this.router.navigateByUrl(targetUrl).then(() => {
        console.log('[Patient Overview] Navigated to:', targetUrl);
      });
    } else {
      console.warn('[Patient Overview] Cannot navigate: missing patientID or facilityID', { patientID, facilityID });
    }

    // Switch to fullscreen mode with the specified URL in the right zone iframe
    const iframeTargetUrl = this.vm.getOrthanC_URL() + '/ui/app/#/';

    // Set up fullscreen mode
    this.vm.isImageShareMode = true;
    this.vm.isIframeLoading = true;
    this.vm.iframeError = null;
    this.vm.selectedImageShareUrl = iframeTargetUrl;
    this.vm.activeImageShareTab = 'orthanc';

    // Ensure share type is set to image (required for fullscreen)
    if (this.vm.shareTypeSelected !== 'image') {
      this.vm.shareTypeSelected = 'image';
    }

    // Set share direction to 'send'
    this.vm.shareDirection = 'send';

    // Make dialog fullscreen
    this.dialogRef?.addPanelClass('fullscreen-dialog');
    this.dialogRef?.updatePosition({ top: '0px', left: '0px', right: '0px', bottom: '0px' });
    this.dialogRef?.updateSize('100vw', '100vh');

    // Clear loading state after a short delay to allow iframe to start loading
    setTimeout(() => {
      this.vm.isIframeLoading = false;
      this.cdr.detectChanges();
    }, 500);

    // Trigger change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Initialize component state from URL path and localStorage
   */
  private initializeFromUrl(): void {
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);

    if (!urlMatch || !urlMatch[1]) return;

    const patientID = urlMatch[1];
    const savedState = this.localStorageService.getPatientOverviewState(patientID);

    // Restore state based on URL path
    if (currentUrl.includes('/exchange')) {
      this.facade.showDecisionScreen = false;
      this.facade.showSharingSetup = true;

      // Restore facility if in URL and saved
      if (currentUrl.includes('/exchange/')) {
        const facilityMatch = currentUrl.match(/\/exchange\/([^\/]+)/);
        if (facilityMatch && facilityMatch[1]) {
          // Try to restore from saved state first
          if (savedState?.selectedTargetFacility) {
            this.facade.onTargetFacilitySelected(savedState.selectedTargetFacility);
          }
        }
      }

      // Restore users if we're past facility selection
      if (savedState?.selectedFacilityUsers && savedState.selectedFacilityUsers.length > 0) {
        this.facade.onFacilityUserSelected(savedState.selectedFacilityUsers);
      }

      // Restore share type if in URL
      if (currentUrl.includes('/images')) {
        this.facade.onShareTypeSelected('image');
      } else if (savedState?.shareTypeSelected) {
        this.facade.onShareTypeSelected(savedState.shareTypeSelected);
      }

      // Restore direction if in URL
      if (currentUrl.includes('/retreive')) {
        this.facade.onReceiveSelected();
      } else if (savedState?.shareDirection) {
        if (savedState.shareDirection === 'send') {
          this.facade.onSendSelected();
        } else {
          this.facade.onReceiveSelected();
        }
      }

      // Set the correct page based on restored state
      // If we're at /exchange/<facilityID> (not at /images yet)
      if (currentUrl.match(/\/exchange\/[^\/]+$/) && !currentUrl.includes('/images')) {
        // Determine the appropriate page based on what's been completed
        if (savedState?.shareTypeSelected) {
          // Share type already selected, go to step 4
          this.facade.currentPage = 4;
        } else if (savedState?.selectedFacilityUsers && savedState.selectedFacilityUsers.length > 0) {
          // Users are selected, advance to step 3 (share type selection)
          this.facade.currentPage = 3;
        } else {
          // No users selected, stay at step 2 (user selection)
          this.facade.currentPage = 2;
        }
      }
    }

    // Defer change detection to avoid "Should be run in update mode" error
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  onSelectedPatientIdEvent(patientId: SharingPatientId): void {
    this.facade.onSelectedPatientIdEvent(patientId);
  }

  onSharingFacilitySelected(patientId: SharingPatientId): void {
    this.facade.onSharingFacilitySelected(patientId);
  }

  onTargetFacilitySelected(patientId: SharingPatientId): void {
    this.facade.onTargetFacilitySelected(patientId);

    // Save to localStorage
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        selectedTargetFacility: patientId
      });
    }

    // Update URL when target facility is selected
    if (urlMatch && urlMatch[1] && patientId.facility) {
      const patientID = urlMatch[1];
      const facilityID = patientId.facility;
      this.router.navigateByUrl(`/patient-overview-modal/${patientID}/exchange/${facilityID}`);
    }
  }

  onFacilityUserSelected(users: any[]): void {
    this.facade.onFacilityUserSelected(users);

    // Save to localStorage
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        selectedFacilityUsers: users
      });
    }

    this.cdr.detectChanges();
  }

  onShareTypeSelected(shareType: 'document' | 'image'): void {
    this.facade.onShareTypeSelected(shareType);

    // Save to localStorage
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        shareTypeSelected: shareType
      });
    }

    // Update URL when IMAGES is selected
    if (shareType === 'image') {
      const urlMatch2 = currentUrl.match(/\/patient-overview-modal\/([^\/]+)\/exchange\/([^\/]+)/);
      if (urlMatch2 && urlMatch2[1] && urlMatch2[2]) {
        const patientID = urlMatch2[1];
        const facilityID = urlMatch2[2];
        this.router.navigateByUrl(`/patient-overview-modal/${patientID}/exchange/${facilityID}/images`);
      }
    }
  }

  generateSharingImageLink(caseName: string, senderFacilityName: string): string {
    return `${environment.config.host}patient-overview-modal/${caseName}/sender/${senderFacilityName}/retreive/images`;
  }

  // NOTE: This only happens with the following flow:
  // 1. User selects a target facility
  // 2. User selects a facilitator
  // 3. User selects IMAGES
  // 4. User clicks on the "Send" button
  onSendSelected(): void {
    /**
     * Prepare the data to share the patient with the target facility
     * This data will be used to send the email notification to the target facility
     * and to mark the patient as shared in the database
     *
     * @param destinationEmails - The emails of the target facility
     * @param action - The action to perform
     * @param messageData - The message data to send
     * @param link - The link to the patient overview
     * @param caseName - The case name of the patient
     */
    this.mailer_$.sendImagesNotificationEmail = {
      destinationEmails: this.vm.selectedFacilityUsers.map(user => user.email),
      action: 'shared-image-access',
      messageData: {
        targetEmail: this.vm.selectedFacilityUsers[0].email,
        caseName: this.vm.patient.caseName,
        files: this.vm.patient.files,
        extraMessage: this.vm.patient.extraMessage,
        link: this.generateSharingImageLink(this.vm.patient.caseName, this.vm.currentFacility)
      }
    };

    /**
     * Prepare the data to share the patient with the target facility
     * This data will be used to mark the patient as shared in the database
     *
     * @param casename - The case name of the patient
     * @param facility - The facility of the target facility
     * @param sharerFacility - The facility of the current facility
     */
    this.firebaseUtilities_$.dataToSharingPatientWithFacility = {
      casename: this.vm.patient.caseName,
      facility: this.vm.selectedTargetFacility.facility,
      sharerFacility: this.vm.currentFacility
    };

    /**
     * Save the state to localStorage
     * This will be used to restore the state when the user reloads the page
     * @param shareDirection - The direction of the share
     */
    // Save to localStorage
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        shareDirection: 'send'
      });
    }
  }

  async onReceiveSelected(): Promise<void> {
    // CRITICAL: Navigate to /retreive FIRST before calling facade.onReceiveSelected()
    // This ensures the pathname includes /retreive when goToFullscreenMode() stores the route
    const currentUrl = this.router.url;
    const urlMatch2 = currentUrl.match(/\/patient-overview-modal\/([^\/]+)\/exchange\/([^\/]+)\/images/);
    if (urlMatch2 && urlMatch2[1] && urlMatch2[2]) {
      const patientID = urlMatch2[1];
      const facilityID = urlMatch2[2];
      // Navigate to /retreive route FIRST
      await this.router.navigateByUrl(`/patient-overview-modal/${patientID}/exchange/${facilityID}/images/retreive`);
      // Wait for navigation to complete and pathname to update
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    // Save to localStorage
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        shareDirection: 'receive'
      });
    }

    // NOW call facade.onReceiveSelected() which will go to fullscreen
    // The pathname will now be correct (includes /retreive) when goToFullscreenMode() stores the route
    await this.facade.onReceiveSelected();
  }

  onShareWithFacility(): void {
    this.facade.onShareWithFacility();
  }

  onShareFromFacility(): void {
    this.facade.onShareFromFacility();
  }

  onSharingGoNext(): void {
    this.facade.onSharingGoNext();
    this.cdr.detectChanges();
  }

  onClose(): void {
    // Check if client profile is open before doing anything
    const clientProfileDialog = this.dialog.openDialogs.find(d => d.id === 'client-profile');

    // If client profile is open, don't close or navigate - let it handle its own lifecycle
    if (clientProfileDialog) {
      console.log('Client profile is open, skipping close and navigation');
      return;
    }

    this.facade.onClose();
    // Only navigate if client profile is not open
    this.router.navigateByUrl('/');
  }

  onBackToPatientsList(): void {
    // Close the dialog and navigate after it's fully closed
    this.dialogRef.afterClosed().subscribe(() => {
      this.router.navigateByUrl('/clientsmatters/external');
    });
    this.dialogRef.close();
  }

  async onDocumentShare(): Promise<void> {
    return this.facade.onDocumentShare();
  }

  onImageShare(): void {
    this.facade.onImageShare();
  }

  onPageChange(page: number): void {
    this.facade.onPageChange(page);
  }

  onRestartSharingSetup(): void {
    this.facade.onRestartSharingSetup();

    // Clear localStorage state
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.clearPatientOverviewState(urlMatch[1]);
    }

    // Reset URL back to patient overview beginning
    if (urlMatch && urlMatch[1]) {
      const patientID = urlMatch[1];
      this.router.navigateByUrl(`/patient-overview-modal/${patientID}/`);
    }

    this.cdr.detectChanges();
  }

  onIframeLoaded(): void {
    this.facade.onIframeLoaded();

    // Cookie-based authentication - no need to inject token via postMessage
    // The cookie is already set and will be sent automatically with requests
    if (this.facade.activeImageShareTab === 'orthanc') {
      console.log('[Patient Overview] Iframe loaded - using cookie-based authentication');
    }
  }

  onIframeError(): void {
    console.error('[Patient Overview] Iframe failed to load');
    this.facade.iframeError = 'Failed to load the viewer. Please check your connection and try again.';
    this.facade.isIframeLoading = false;
  }

  /**
   * Cookie-based authentication - token is already in cookie, no injection needed
   * This method is kept for potential future use or fallback scenarios
   */
  private async injectTokenIntoIframe(): Promise<void> {
    // Cookie-based auth - token is already in cookie, no injection needed
    // The browser automatically sends cookies with requests to the same origin
    // or with proper CORS configuration
    console.log('[Patient Overview] Using cookie-based authentication - no injection needed');
  }

  get activeImageShareTitle(): string {
    return this.facade.activeImageShareTitle;
  }

  get vm(): OldPatientOverviewFacade {
    return this.facade;
  }

  get sourceFacility(): FacilityInfo | undefined {
    if (!this.vm.currentFacilityPatientId) {
      return undefined;
    }
    const facilityName = this.vm.currentFacility || 'Current Facility';
    return {
      name: facilityName,
      patientId: this.vm.currentFacilityPatientId.value
    };
  }

  get destinationFacility(): FacilityInfo | undefined {
    if (!this.vm.selectedTargetFacility) {
      return undefined;
    }
    return {
      name: this.vm.selectedTargetFacility.facility,
      patientId: this.vm.selectedTargetFacility.value
    };
  }

  get facilitators(): FacilitatorInfo[] {
    // Return all selected users as facilitators
    if (!this.vm.selectedFacilityUsers || this.vm.selectedFacilityUsers.length === 0) {
      return [];
    }
    return this.vm.selectedFacilityUsers.map(user => ({
      name: user.name,
      email: user.email || ''
    }));
  }

  get exchangeMode(): 'send' | 'receive' | null {
    return this.vm.shareDirection;
  }

  get shouldShowSendInstructions(): boolean {
    // Only show in fullscreen mode
    if (!this.vm.isImageShareMode) {
      return false;
    }

    // Check if URL matches the pattern: /patient-overview-modal/<patientId>/exchange/<targetFacilityId>/images/send
    const currentUrl = this.router.url;
    const urlPattern = /^\/patient-overview-modal\/[^\/]+\/exchange\/[^\/]+\/images\/send$/;
    return urlPattern.test(currentUrl);
  }

  get shouldShowRetrieveInstructions(): boolean {
    // Only show in fullscreen mode
    if (!this.vm.isImageShareMode) {
      return false;
    }

    // Check if URL route ends in /retreive
    const currentUrl = this.router.url;
    return currentUrl.endsWith('/retreive');
  }

  onDecisionSelected(option: 'profile' | 'library' | 'exchange'): void {
    this.facade.onDecisionSelected(option);

    // Update URL when "Images and Documents Exchange" is selected
    if (option === 'exchange') {
      const currentUrl = this.router.url;
      // Extract patientID from current URL (format: /patient-overview-modal/<patientID>)
      const urlMatch = currentUrl.match(/\/patient-overview-modal\/([^\/]+)/);
      if (urlMatch && urlMatch[1]) {
        const patientID = urlMatch[1];
        this.router.navigateByUrl(`/patient-overview-modal/${patientID}/exchange`);
      } else if (this.vm.patient?.patientId) {
        // Fallback: use patientId from patient data if URL parsing fails
        this.router.navigateByUrl(`/patient-overview-modal/${this.vm.patient.patientId}/exchange`);
      }
    }

    this.cdr.detectChanges();
  }


  onCreateFacilityUser(): void {
    const targetFacility = this.vm.selectedTargetFacility;
    if (!targetFacility) {
      return;
    }

    // Get ownerID from patient data or auth service
    // Priority: patient.ownerID > patient.owners[0] > auth service ownerID
    let ownerID: string | undefined;

    if (this.vm.patient?.ownerID) {
      ownerID = this.vm.patient.ownerID;
    } else if (this.vm.patient?.owners && this.vm.patient.owners.length > 0) {
      ownerID = this.vm.patient.owners[0];
    }
    // If still no ownerID, it will be handled by CreateFacilityUserComponent's ngOnInit

    // The facility identifier should be the facility name (lowercased), not the patient ID
    // targetFacility.facility contains the facility name (e.g., "wade-wilson-hospital")
    // targetFacility.value contains the patient ID (e.g., "wwh-jb-001")

    const dialogRef = this.dialog.open(CreateFacilityUserComponent, {
      width: '600px',
      data: {
        title: 'Create Facility User',
        skipFacilitySelection: true,
        preSelectedFacility: {
          name: targetFacility.facility,
          id: targetFacility.facility.toLowerCase().replace(/\s+/g, '-'), // Use facility name as ID
          ownerID: ownerID // Include ownerID if available
        },
        role: 'admin', // Default to admin/facilitator
        limits: {
          limits: {
            limits: {
              users: {
                admin: 999,
                facility: 999,
                consultant: 999,
                associate: 999
              }
            },
            users: {
              admin: [],
              facility: [],
              consultant: [],
              associate: []
            }
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('User created:', result);

        // Add the newly created user to the SharingSetupComponent's user list
        if (this.sharingSetupComponent) {
          this.sharingSetupComponent.addNewUser({
            id: result.id || result.email, // Use id if available, otherwise email
            name: result.name,
            email: result.email,
            role: result.role === 'admin' ? 'Facilitator' : result.role,
            facility: result.facility
          });

          console.log('New user added to available facilitators list');
        } else {
          console.warn('SharingSetupComponent not available to update user list');
        }
      }
    });
  }

  /**
   * Cleanup event listeners when component is destroyed
   */
  onStudiesChanged(studies: any[]): void {
    if (!studies || studies.length === 0) {
      this.studiesHaveMultiplePatients = false;
      this.studiesFoundPatient = null;
      return;
    }
    const firstId = studies[0].patientId;
    this.studiesHaveMultiplePatients = !studies.every((s: any) => s.patientId === firstId);

    if (!this.studiesHaveMultiplePatients) {
      const s = studies[0];
      const nameParts = (s.patientName || '').split('^');
      const lastname = nameParts[0] || '';
      const firstname = nameParts[1] || '';
      let dob = s.patientBirthDate || '';
      if (dob.length === 8 && !dob.includes('-')) {
        dob = `${dob.substring(0, 4)}-${dob.substring(4, 6)}-${dob.substring(6, 8)}`;
      }
      this.studiesFoundPatient = {
        clientname: '',
        firstname,
        lastname,
        dob,
        gender: s.patientSex || '',
        patientId: s.patientId || '',
        caseName: s.patientId || '',
        ownerID: s.patientId || '',
        type: 'medical'
      };
    } else {
      this.studiesFoundPatient = null;
    }
  }

  ngOnDestroy(): void {
    if (this.backToPatientOverviewHandler) {
      window.removeEventListener('nuagedx-back-to-patient-overview', this.backToPatientOverviewHandler);
    }
  }
}
